import { type Component, type ComponentProps, For } from "solid-js";
import { useLocation } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Home, Library, ScanLine, Store, User } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { stripUPrefix } from "@/lib/active-account";
import { pressable, SPRINGS, type TabGroups } from "@/lib/motion";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@famidex/shared";

interface Tab {
  to: string;
  label: MessageDescriptor;
  icon: Component<ComponentProps<"svg">>;
  /** Prefijos de ruta que iluminan este tab (rutas anidadas del módulo). */
  match: string[];
}

// Los 4 tabs canónicos del shell FamiDex (design: bottom-bar «navegación
// actualizada»). El shell los POSEE; las agentes de Wave 1 cablean sus rutas
// DENTRO de estos tabs, no editan la barra.
const TABS: Tab[] = [
  { to: "/", label: msg`Aldea`, icon: Home, match: ["/"] },
  { to: "/collection", label: msg`Colección`, icon: Library, match: ["/collection", "/catalog"] },
  { to: "/market", label: msg`Mercado`, icon: Store, match: ["/market"] },
  { to: "/journal", label: msg`Perfil`, icon: User, match: ["/journal"] },
];

/**
 * Grupos de prefijos de ruta en el ORDEN de la barra — alimenta la dirección
 * de las transiciones laterales de página (`<MotionOutlet tabs={…}>`).
 */
export const TAB_GROUPS: TabGroups = TABS.map((tab) => tab.match);

function isActive(pathname: string, tab: Tab): boolean {
  const inner = stripUPrefix(pathname) || "/";
  if (tab.to === "/") {
    return inner === "/";
  }
  return tab.match.some((m) => inner === m || inner.startsWith(`${m}/`));
}

/**
 * Barra de navegación inferior — 4 tabs (Aldea/Colección/Mercado/Perfil) + FAB
 * de escáner. Reemplaza el sidebar del template. Fija abajo, respeta el safe-area
 * y se oculta al imprimir. Los `<Link>` de `@/lib/u-router` re-prefijan `/u/<n>`.
 *
 * Movimiento (lib/motion, GPU-only): pill activa que entra con resorte tras el
 * icono, pop del icono al activarse, y press-scale con háptica en tabs y FAB.
 */
export function BottomNav() {
  const location = useLocation();
  const { t } = useI18n();

  return (
    <>
      {/* FAB escáner — atajo directo al corazón del flujo 🎯 (scan → detalle → añadir). */}
      <Link
        to="/scanner"
        aria-label={t(msg`Escanear código de barras`)}
        data-testid="scanner-fab"
        ref={(el: HTMLAnchorElement) =>
          pressable(el, { scale: 0.88, hapticMs: 8, releaseConfig: SPRINGS.pop })
        }
        class="fixed bottom-24 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl"
      >
        <ScanLine class="h-7 w-7" />
      </Link>

      <nav
        aria-label={t(msg`Navegación principal`)}
        class="fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border-t border-border bg-card/95 backdrop-blur-md pb-[var(--safe-area-inset-bottom)] shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
      >
        <ul class="mx-auto flex max-w-2xl items-center justify-around px-2 pt-2 pb-1">
          <For each={TABS}>
            {(tab) => {
              const active = () => isActive(location().pathname, tab);
              return (
                <li>
                  <Link
                    to={tab.to}
                    data-testid={`tab-${tab.to === "/" ? "home" : tab.to.slice(1)}`}
                    aria-current={active() ? "page" : undefined}
                    ref={(el: HTMLAnchorElement) => pressable(el, { scale: 0.92, hapticMs: 5 })}
                    class={cn(
                      "group flex min-w-16 flex-col items-center gap-1 rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors",
                      active() ? "text-primary" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <span class="relative grid place-items-center" data-active={active()}>
                      {/* Pill activa: entra con resorte (transform/opacity, GPU). */}
                      <span
                        aria-hidden="true"
                        data-active={active()}
                        class="motion-tab-pill absolute -inset-x-3 -inset-y-1 rounded-full bg-primary/12"
                      />
                      <tab.icon
                        class={cn(
                          "motion-tab-icon relative h-6 w-6",
                          active() && "fill-primary/15",
                        )}
                      />
                    </span>
                    <span>{t(tab.label)}</span>
                  </Link>
                </li>
              );
            }}
          </For>
        </ul>
      </nav>
    </>
  );
}
